import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PersonIcon from '@mui/icons-material/Person';
import { toast } from 'react-toastify';
import { faceApi } from '../../../api/faceApi';
import { attendanceApi } from '../../../api/attendanceApi';

type KioskMode = 'idle' | 'capturing' | 'verifying' | 'punchtype' | 'success' | 'error';

interface VerifiedEmployee {
  id: string;
  name: string;
  code: string;
  confidence: number;
}

const AUTO_CAPTURE_COOLDOWN_MS = 7000;
const FACE_SCAN_INTERVAL_MS = 650;
const STABLE_FACE_DETECTIONS_REQUIRED = 2;
const FACE_SAMPLE_WIDTH = 96;
const FACE_SAMPLE_HEIGHT = 72;

/**
 * Kiosk Page for Anonymous Face-based Attendance
 * Allows employees to check in/out without authentication using face recognition
 * Suitable for shared kiosk devices
 */
export const KioskPage: React.FC = () => {
  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const faceScanTimeoutRef = useRef<number | null>(null);
  const stableFaceDetectionsRef = useRef(0);
  const lastAutoCaptureAtRef = useRef(0);

  // State
  const [mode, setMode] = useState<KioskMode>('idle');
  const [isCameraActive, setIsCameraActive] = useState(true);
  const [verifiedEmployee, setVerifiedEmployee] = useState<VerifiedEmployee | null>(null);
  const [lastPunch, setLastPunch] = useState<{ type: string; time: Date } | null>(null);
  const [resultMessage, setResultMessage] = useState('');
  const [manualEmployeeId, setManualEmployeeId] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [idleTimeout, setIdleTimeout] = useState<NodeJS.Timeout | null>(null);
  const [faceScanStatus, setFaceScanStatus] = useState('Looking for face...');
  const formatTime = (date: Date) => date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const speak = (message: string) => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(message);
    utterance.rate = 0.95;
    window.speechSynthesis.speak(utterance);
  };

  const getApiMessage = (error: any, fallback: string) =>
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback;

  const clearFaceScanTimeout = () => {
    if (faceScanTimeoutRef.current !== null) {
      window.clearTimeout(faceScanTimeoutRef.current);
      faceScanTimeoutRef.current = null;
    }
  };

  // Start camera
  const startCamera = async () => {
    try {
      stopCamera();
      setIsCameraActive(false);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setIsCameraActive(true);
      }
    } catch (error) {
      setIsCameraActive(false);
      toast.error('Camera not available');
      console.error('Camera error:', error);
    }
  };

  // Stop camera
  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
  };

  // Reset to idle
  const resetToIdle = () => {
    setMode('idle');
    setVerifiedEmployee(null);
    setResultMessage('');
    setManualEmployeeId('');

    // Clear idle timeout
    if (idleTimeout) clearTimeout(idleTimeout);

    // Set new idle timeout (2 minutes)
    const timeout = setTimeout(() => {
      setLastPunch(null);
      resetToIdle();
    }, 120000);

    setIdleTimeout(timeout);
    window.setTimeout(() => {
      void startCamera();
    }, 100);
  };

  // Verify face
  const verifyFace = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    clearFaceScanTimeout();
    stableFaceDetectionsRef.current = 0;
    lastAutoCaptureAtRef.current = Date.now();
    setMode('capturing');

    try {
      // Capture image
      const context = canvasRef.current.getContext('2d');
      if (context && videoRef.current.videoWidth && videoRef.current.videoHeight) {
        canvasRef.current.width = videoRef.current.videoWidth;
        canvasRef.current.height = videoRef.current.videoHeight;
        context.drawImage(videoRef.current, 0, 0);
        const faceImageBase64 = canvasRef.current.toDataURL('image/jpeg', 0.6);
        stopCamera();

        setMode('verifying');

        // Verify face
        try {
          const result = await faceApi.verifyFace({
            faceImageBase64,
            matchThreshold: 0.65,
          });

          if (result.isVerified && result.employeeId) {
            const employee = {
              id: result.employeeId,
              name: result.employeeName || 'Unknown',
              code: result.employeeCode || '',
              confidence: result.confidence || 0.9,
            };
            setVerifiedEmployee(employee);
            speak(`Welcome ${employee.name}`);
            await performAutoPunch(employee, faceImageBase64);
          } else {
            setMode('error');
            const message = result.errorMessage || 'Face not registered';
            setResultMessage(message);
            speak(message);
            toast.warning(message);
            setTimeout(() => {
              resetToIdle();
            }, 3000);
          }
        } catch (error: any) {
          setMode('error');
          const message = getApiMessage(error, 'Verification failed');
          setResultMessage(message);
          speak(message);
          toast.error(message);
          setTimeout(() => {
            resetToIdle();
          }, 3000);
        }
      } else {
        throw new Error('Camera frame is not ready');
      }
    } catch (error) {
      setMode('error');
      setResultMessage('Failed to capture face');
      speak('Failed to capture face');
      toast.error('Failed to capture face');
      setTimeout(() => {
        resetToIdle();
      }, 3000);
    }
  };

  const hasLikelyFaceInFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth || !video.videoHeight) return false;

    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return false;

    canvas.width = FACE_SAMPLE_WIDTH;
    canvas.height = FACE_SAMPLE_HEIGHT;
    context.drawImage(video, 0, 0, FACE_SAMPLE_WIDTH, FACE_SAMPLE_HEIGHT);

    const image = context.getImageData(0, 0, FACE_SAMPLE_WIDTH, FACE_SAMPLE_HEIGHT).data;
    let skinLikePixels = 0;
    let usablePixels = 0;
    let lumaSum = 0;
    let lumaSquaredSum = 0;

    const startX = Math.floor(FACE_SAMPLE_WIDTH * 0.2);
    const endX = Math.floor(FACE_SAMPLE_WIDTH * 0.8);
    const startY = Math.floor(FACE_SAMPLE_HEIGHT * 0.12);
    const endY = Math.floor(FACE_SAMPLE_HEIGHT * 0.82);

    for (let y = startY; y < endY; y += 1) {
      for (let x = startX; x < endX; x += 1) {
        const offset = (y * FACE_SAMPLE_WIDTH + x) * 4;
        const r = image[offset];
        const g = image[offset + 1];
        const b = image[offset + 2];
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        const cb = 128 - 0.168736 * r - 0.331264 * g + 0.5 * b;
        const cr = 128 + 0.5 * r - 0.418688 * g - 0.081312 * b;

        if (luma > 25 && luma < 245) {
          usablePixels += 1;
          lumaSum += luma;
          lumaSquaredSum += luma * luma;
        }

        if (cb >= 75 && cb <= 145 && cr >= 125 && cr <= 185 && luma > 35) {
          skinLikePixels += 1;
        }
      }
    }

    if (usablePixels === 0) return false;

    const mean = lumaSum / usablePixels;
    const variance = Math.max(0, lumaSquaredSum / usablePixels - mean * mean);
    const standardDeviation = Math.sqrt(variance);
    const skinRatio = skinLikePixels / usablePixels;

    return skinRatio >= 0.04 && standardDeviation >= 16;
  };

  const scanForFace = async () => {
    clearFaceScanTimeout();

    if (mode !== 'idle' || !isCameraActive) return;

    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      setFaceScanStatus('Camera is getting ready...');
      faceScanTimeoutRef.current = window.setTimeout(() => {
        void scanForFace();
      }, FACE_SCAN_INTERVAL_MS);
      return;
    }

    const elapsedSinceLastCapture = Date.now() - lastAutoCaptureAtRef.current;
    if (elapsedSinceLastCapture < AUTO_CAPTURE_COOLDOWN_MS) {
      setFaceScanStatus('Please step away for the next punch...');
      faceScanTimeoutRef.current = window.setTimeout(() => {
        void scanForFace();
      }, AUTO_CAPTURE_COOLDOWN_MS - elapsedSinceLastCapture);
      return;
    }

    try {
      if (hasLikelyFaceInFrame()) {
        stableFaceDetectionsRef.current += 1;
        setFaceScanStatus('Face detected. Capturing...');

        if (stableFaceDetectionsRef.current >= STABLE_FACE_DETECTIONS_REQUIRED) {
          void verifyFace();
          return;
        }
      } else {
        stableFaceDetectionsRef.current = 0;
        setFaceScanStatus('Looking for face...');
      }
    } catch (error) {
      stableFaceDetectionsRef.current = 0;
      setFaceScanStatus('Looking for face...');
      console.error('Face scan error:', error);
    }

    faceScanTimeoutRef.current = window.setTimeout(() => {
      void scanForFace();
    }, FACE_SCAN_INTERVAL_MS);
  };

  const performAutoPunch = async (employee: VerifiedEmployee, faceImageBase64: string) => {
    setMode('verifying');

    try {
      const status = await attendanceApi.getTodayStatus(employee.id);
      const shouldCheckOut = Boolean(status.isCheckedIn);

      if (shouldCheckOut) {
        await attendanceApi.checkOutAnonymous(
          employee.id,
          'Kiosk - Face Recognition',
          undefined,
          faceImageBase64
        );

        setLastPunch({ type: 'CHECK_OUT', time: new Date() });
        setResultMessage(`Welcome ${employee.name}. Check-out successful`);
        speak(`Check-out successful`);
        toast.success(`${employee.name} checked out successfully`);
      } else {
        await attendanceApi.checkInAnonymous(
          employee.id,
          'Kiosk - Face Recognition',
          undefined,
          faceImageBase64
        );

        setLastPunch({ type: 'CHECK_IN', time: new Date() });
        setResultMessage(`Welcome ${employee.name}. Check-in successful`);
        speak(`Check-in successful`);
        toast.success(`${employee.name} checked in successfully`);
      }

      setMode('success');
      setTimeout(() => {
        resetToIdle();
      }, 3500);
    } catch (error: any) {
      const message = getApiMessage(error, 'Punch failed');
      const normalized = message.toLowerCase();
      const voiceMessage = normalized.includes('already checked in')
        ? 'Duplicate check-in'
        : normalized.includes('already checked out')
          ? 'Duplicate check-out'
          : message;

      setResultMessage(voiceMessage);
      speak(voiceMessage);
      toast.error(voiceMessage);
      setMode('error');
      setTimeout(() => {
        resetToIdle();
      }, 3500);
    }
  };

  // Perform punch
  const performPunch = async (punchType: 'CHECK_IN' | 'CHECK_OUT') => {
    if (!verifiedEmployee) return;

    setMode('verifying');

    try {
      if (punchType === 'CHECK_IN') {
        await attendanceApi.checkInAnonymous(
          verifiedEmployee.id,
          'Kiosk - Face Recognition'
        );

        setLastPunch({
          type: 'CHECK_IN',
          time: new Date(),
        });

        toast.success(`${verifiedEmployee.name} checked in successfully`);
      } else {
        await attendanceApi.checkOutAnonymous(
          verifiedEmployee.id,
          'Kiosk - Face Recognition'
        );

        setLastPunch({
          type: 'CHECK_OUT',
          time: new Date(),
        });

        toast.success(`${verifiedEmployee.name} checked out successfully`);
      }

      setMode('success');
      setTimeout(() => {
        resetToIdle();
      }, 3000);
    } catch (error: any) {
      setMode('error');
      const message = getApiMessage(error, 'Punch failed');
      setResultMessage(message);
      speak(message);
      toast.error(message);
      setTimeout(() => {
        resetToIdle();
      }, 3000);
    }
  };

  // Handle manual entry
  const handleManualEntry = async () => {
    if (!manualEmployeeId) {
      toast.warning('Please enter employee ID');
      return;
    }

    setShowManualEntry(false);
    try {
      // Try to identify employee by ID and proceed
      // For now, just reset
      setManualEmployeeId('');
      resetToIdle();
    } catch (error) {
      toast.error('Employee not found');
    }
  };

  // Initialize camera
  useEffect(() => {
    startCamera();

    return () => {
      clearFaceScanTimeout();
      stopCamera();
      if (idleTimeout) clearTimeout(idleTimeout);
    };
  }, []);

  useEffect(() => {
    clearFaceScanTimeout();
    stableFaceDetectionsRef.current = 0;

    if (mode === 'idle' && isCameraActive) {
      setFaceScanStatus('Looking for face...');
      faceScanTimeoutRef.current = window.setTimeout(() => {
        void scanForFace();
      }, FACE_SCAN_INTERVAL_MS);
    }

    return clearFaceScanTimeout;
  }, [mode, isCameraActive]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
      }}
    >
      <Container maxWidth="sm">
        <Card sx={{ boxShadow: 4 }}>
          <CardContent sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                ATTENDANCE KIOSK
              </Typography>
              <Typography variant="h6" color="textSecondary">
                Face Recognition Check In/Out
              </Typography>
            </Box>

            {mode === 'idle' && (
              <>
                {/* Camera Feed */}
                <Paper sx={{ bgcolor: '#000', position: 'relative', mb: 3, overflow: 'hidden' }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: '100%',
                      height: 'auto',
                      minHeight: 350,
                      display: 'block',
                    }}
                  />
                  {!isCameraActive && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#000',
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )}

                  {/* Instruction Overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 20,
                      left: 0,
                      right: 0,
                      textAlign: 'center',
                      color: 'white',
                    }}
                  >
                    <Typography variant="h6">Position your face</Typography>
                  </Box>
                </Paper>

                {/* Last Punch Info */}
                {lastPunch && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Last punch: {lastPunch.type} at {formatTime(lastPunch.time)}
                  </Alert>
                )}

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <CircularProgress size={26} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {faceScanStatus}
                  </Typography>
                </Box>
              </>
            )}

            {mode === 'capturing' && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={60} sx={{ mb: 2 }} />
                <Typography variant="h6">Capturing face...</Typography>
              </Box>
            )}

            {mode === 'verifying' && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={60} sx={{ mb: 2 }} />
                <Typography variant="h6">Verifying identity...</Typography>
              </Box>
            )}

            {mode === 'punchtype' && verifiedEmployee && (
              <Box>
                {/* Employee Info */}
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
                  <PersonIcon sx={{ fontSize: 40, mb: 1, color: 'primary.main' }} />
                  <Typography variant="h6" gutterBottom>
                    {verifiedEmployee.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {verifiedEmployee.code}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Confidence: {(verifiedEmployee.confidence * 100).toFixed(0)}%
                  </Typography>
                </Box>

                {/* Punch Options */}
                <Stack spacing={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    size="large"
                    onClick={() => performPunch('CHECK_IN')}
                    sx={{ py: 1.5, fontSize: '1rem' }}
                  >
                    ✓ CHECK IN
                  </Button>

                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    size="large"
                    onClick={() => performPunch('CHECK_OUT')}
                    sx={{ py: 1.5, fontSize: '1rem' }}
                  >
                    ✗ CHECK OUT
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={resetToIdle}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Box>
            )}

            {mode === 'success' && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Success!
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {resultMessage || 'Punch recorded successfully'}
                </Typography>
              </Box>
            )}

            {mode === 'error' && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Failed
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {resultMessage || 'Please try again'}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'none' }}>
              <canvas ref={canvasRef} />
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Manual Entry Dialog */}
      <Dialog open={showManualEntry} onClose={() => setShowManualEntry(false)}>
        <DialogTitle>Enter Employee ID</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Employee ID or Code"
            value={manualEmployeeId}
            onChange={(e) => setManualEmployeeId(e.target.value)}
            margin="dense"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleManualEntry();
              }
            }}
          />
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button onClick={() => setShowManualEntry(false)}>Cancel</Button>
          <Button onClick={handleManualEntry} variant="contained">
            Proceed
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default KioskPage;
